<template>
  <organism-qr-access-dashboard
    :fields="fields"
    :todayStatusCountList="todayStatusCountList"
    :todaySiteApprovedList="todaySiteApprovedList"
    :isLoadingTodaySiteApproved="isLoadingTodaySiteApproved"
    :todaySiteAccessList="todaySiteAccessList"
    :isLoadingTodaySiteAccess="isLoadingTodaySiteAccess"
    :siteAccessStatusCountList="siteAccessStatusCountList"
    :isLoadingSiteAccessStatusCount="isLoadingSiteAccessStatusCount"
    :todaySelfCheckInList="todaySelfCheckInList"
    :isLoadingTodaySelfCheckIn="isLoadingTodaySelfCheckIn"
    :pastSiteAccessList="pastSiteAccessList"
    :isLoadingPastSiteAccess="isLoadingPastSiteAccess"
    :filterList="filterData"
    @selectRowTableSiteAccessDashboard="selectRowTableSiteAccessDashboard"
    @changeCurrentPageTodaySiteApproved="changeCurrentPageTodaySiteApproved"
    @changeCurrentPageTodaySiteAccess="changeCurrentPageTodaySiteAccess"
    @changeCurrentPagePastSiteAccess="changeCurrentPagePastSiteAccess"
  ></organism-qr-access-dashboard>
</template>

<script>
import { DateTime } from "luxon"
import { mapGetters, mapActions } from "vuex"
import {
  getTodayStatusCount,
  getTodaySiteAccessStatus,
  getTodaySelfCheckIn,
  getTodaySiteApproved,
  getTodaySiteAccess,
  getPastSiteAccess,
} from "@/api/module-1/site-access"
import OrganismQrAccessDashboard from "@/components/organisms/portal/internal/qrAccess/OrganismQrAccessDashboard.vue"
import mixins from "@/mixins/index"
import mixinsSiteHelper from "@/mixins/mixinsSiteHelper"
import mixinsDatetime from "@/mixins/mixinsDatetime"

export default {
  name: "QrAccessDashboard",
  components: { OrganismQrAccessDashboard },
  mixins: [mixins, mixinsSiteHelper, mixinsDatetime],
  data() {
    return {
      fields: [
        {
          key: "id",
          label: "Site Access ID",
          thClass: "w-100px",
          tdClass: "w-100px",
        },
        {
          key: "site",
          label: "Site",
          thClass: "w-100px",
          tdClass: "w-100px",
        },
        {
          key: "requestStart",
          label: "Request Start Date",
          thClass: "w-100px",
          tdClass: "w-100px",
        },
        {
          key: "requestEnd",
          label: "Request End Date",
          thClass: "w-100px",
          tdClass: "w-100px",
        },
        {
          key: "latestCheckIn",
          label: "Latest Check In Date",
          thClass: "w-100px",
          tdClass: "w-100px",
        },
        {
          key: "latestCheckOut",
          label: "Latest Check Out Date",
          thClass: "w-100px",
          tdClass: "w-100px",
        },
        {
          key: "visitors",
          label: "Visitors",
          thClass: "w-250px",
          tdClass: "w-250px",
        },
      ],

      // TODAY STATUS COUNT
      todayStatusCountList: [
        {
          key: "todayRequest",
          titleName: "Today Request",
          requestCount: 0,
          subText: "Site Access Request",
          isLoading: true,
        },
        {
          key: "todaySelfCheckIn",
          titleName: "Today Self Check-IN",
          personCount: 0,
          subText: "Person",
          isLoading: true,
        },
      ],

      // TODAY SITE ACCESS (APPROVED)
      isLoadingTodaySiteApproved: false,
      todaySiteApprovedList: [],

      // TODAY SITE ACCESS (ACCESS)
      isLoadingTodaySiteAccess: false,
      todaySiteAccessList: [],

      // SITE ACCESS STATUS COUNT
      isLoadingSiteAccessStatusCount: false,
      siteAccessStatusCountList: [
        {
          title: "Waiting",
          value: 0,
        },
        {
          title: "Approved",
          value: 0,
        },
        {
          title: "Access",
          value: 0,
        },
        {
          title: "Closed",
          value: 0,
        },
        {
          title: "Total",
          value: 0,
        },
      ],

      // TODAY SELF CHECK-IN
      isLoadingTodaySelfCheckIn: true,
      todaySelfCheckInList: [],

      // PAST SITE ACCESS
      isLoadingPastSiteAccess: false,
      pastSiteAccessList: [],

      filterData: {
        currentPageTodaySiteApproved: 1,
        totalCountTodaySiteApproved: 0,
        currentPageTodaySiteAccess: 1,
        totalCountTodaySiteAccess: 0,
        currentPagePastSiteAccess: 1,
        totalCountPastSiteAccess: 0,
      },

      intervalId: 0,
    }
  },
  computed: {
    ...mapGetters("siteAccess", ["getSiteSelect"]),
    ...mapGetters("appAutoRefresh", ["getWPMSiteAccessDashboardAutoRefresh"]),

    ...mapGetters("siteStore", ["siteSetting", "siteOptions"]),

    isEnableAutoRefresh() {
      return this.getWPMSiteAccessDashboardAutoRefresh
    },
  },

  async mounted() {
    this.setIsShowAutoRefresh(true)
    await this.getSiteAccessDashboardDataFromApi()
  },

  beforeDestroy() {
    this.setIsShowAutoRefresh(false)
    window.clearInterval(this.intervalId)
  },

  watch: {
    async siteSetting() {
      await this.getSiteAccessDashboardDataFromApi()
    },

    isEnableAutoRefresh: {
      deep: true,
      immediate: true,
      handler() {
        if (this.isEnableAutoRefresh) {
          this.intervalId = window.setInterval(() => {
            this.getSiteAccessDashboardDataFromApi()
          }, 60000)
        } else {
          window.clearInterval(this.intervalId)
        }
      },
    },
  },

  methods: {
    ...mapActions("appAutoRefresh", ["setIsShowAutoRefresh"]),

    async getSiteAccessDashboardDataFromApi() {
      await Promise.all([
        this.getTodayStatusCountFromApi(),
        this.getTodaySiteAccessStatusFromApi(),
        this.getTodaySelfCheckInFromApi(),
        this.getTodaySiteApprovedFromApi(),
        this.getTodaySiteAccessFromApi(),
        this.getPastSiteAccessFromApi(),
      ])
    },

    async getTodayStatusCountFromApi() {
      await getTodayStatusCount({
        siteId: this.siteOptions.map((site) => site.value),
      }).then((response) =>
        this.todayStatusCountList.forEach((item) => {
          switch (item.key) {
            case "todayRequest":
              item.requestCount = response.data.requestPeriod || 0
              item.isLoading = false
              break
            case "todaySelfCheckIn":
              item.personCount = response.data.checkIn || 0
              item.isLoading = false
              break
          }
        })
      )
    },

    async getTodaySiteAccessStatusFromApi() {
      this.isLoadingSiteAccessStatusCount = true
      await getTodaySiteAccessStatus({
        siteId: this.siteOptions.map((site) => site.value),
      })
        .then((response) => {
          this.siteAccessStatusCountList.forEach((item) => {
            switch (item.title) {
              case "Waiting":
                item.value = response.data.waiting || 0
                break
              case "Approved":
                item.value = response.data.approved || 0
                break
              case "Access":
                item.value = response.data.access || 0
                break
              case "Closed":
                item.value = response.data.closed || 0
                break
              case "Total":
                item.value = response.data.total || 0
                break
            }
          })
        })
        .finally(() => (this.isLoadingSiteAccessStatusCount = false))
    },

    async getTodaySelfCheckInFromApi() {
      this.isLoadingTodaySelfCheckIn = true
      await getTodaySelfCheckIn({
        siteId: this.siteOptions.map((site) => site.value),
      })
        .then((response) => {
          this.todaySelfCheckInList = response.data.visitorCheckin.map(
            (item) => {
              return {
                accessVisitorId: item.accessVisitorId,
                imgUrl: item.image,
                name: `${item.firstname} ${item.lastname}`,
                identity: item.identity,
                checkInDate: this.setDateTimeFormat(
                  item.checkInDate,
                  "dd/MM/yyyy HH:mm"
                ),
                siteName: item.siteName,
                siteAccessId: item.siteAccessId,
              }
            }
          )
        })
        .finally(() => (this.isLoadingTodaySelfCheckIn = false))
    },

    async getTodaySiteApprovedFromApi() {
      this.isLoadingTodaySiteApproved = true
      const filterData = this.filterDecoration("approved")
      await getTodaySiteApproved(filterData)
        .then((response) => {
          this.filterData.totalCountTodaySiteApproved = response.totalCount
          this.todaySiteApprovedList = response.data.map((item) => {
            return {
              id: {
                siteAccessId: item.siteAccessId,
                idcId: item.idcId?.toString().padStart(8, "0"),
                status: item.status,
              },
              site: item.siteName,
              requestStart: {
                date: this.dateFormat(item.requestStartDate),
                time: this.timeFormat(item.requestStartDate),
              },
              requestEnd: {
                date: this.dateFormat(item.requestEndDate),
                time: this.timeFormat(item.requestEndDate),
              },
              latestCheckIn: {
                date: this.dateFormat(item.checkInDate),
                time: this.timeFormat(item.checkInDate),
              },
              latestCheckOut: {
                date: this.dateFormat(item.checkOutDate),
                time: this.timeFormat(item.checkOutDate),
              },
              visitors: item.visitors.map(
                (visitor) => `${visitor.firstname} ${visitor.lastname}`
              ),
            }
          })
        })
        .finally(() => (this.isLoadingTodaySiteApproved = false))
    },

    async getTodaySiteAccessFromApi() {
      this.isLoadingTodaySiteAccess = true
      const filterData = this.filterDecoration("access")
      await getTodaySiteAccess(filterData)
        .then((response) => {
          this.filterData.totalCountTodaySiteAccess = response.totalCount
          this.todaySiteAccessList = response.data.map((item) => {
            return {
              id: {
                siteAccessId: item.siteAccessId,
                idcId: item.idcId?.toString().padStart(8, "0"),
                status: item.status,
              },
              site: item.siteName,
              requestStart: {
                date: this.dateFormat(item.requestStartDate),
                time: this.timeFormat(item.requestStartDate),
              },
              requestEnd: {
                date: this.dateFormat(item.requestEndDate),
                time: this.timeFormat(item.requestEndDate),
              },
              latestCheckIn: {
                date: this.dateFormat(item.checkInDate),
                time: this.timeFormat(item.checkInDate),
              },
              latestCheckOut: {
                date: this.dateFormat(item.checkOutDate),
                time: this.timeFormat(item.checkOutDate),
              },
              visitors: item.visitors.map(
                (visitor) => `${visitor.firstname} ${visitor.lastname}`
              ),
            }
          })
        })
        .finally(() => (this.isLoadingTodaySiteAccess = false))
    },

    async getPastSiteAccessFromApi() {
      this.isLoadingPastSiteAccess = true
      const filterData = this.filterDecoration("past")
      await getPastSiteAccess(filterData)
        .then((response) => {
          this.filterData.totalCountPastSiteAccess = response.totalCount
          this.pastSiteAccessList = response.data.map((item) => {
            return {
              id: {
                siteAccessId: item.siteAccessId,
                idcId: item.idcId?.toString().padStart(8, "0"),
                status: item.status,
              },
              site: item.siteName,
              requestStart: {
                date: this.dateFormat(item.requestStartDate),
                time: this.timeFormat(item.requestStartDate),
              },
              requestEnd: {
                date: this.dateFormat(item.requestEndDate),
                time: this.timeFormat(item.requestEndDate),
              },
              latestCheckIn: {
                date: this.dateFormat(item.checkInDate),
                time: this.timeFormat(item.checkInDate),
              },
              latestCheckOut: {
                date: this.dateFormat(item.checkOutDate),
                time: this.timeFormat(item.checkOutDate),
              },
              visitors: item.visitors.map(
                (visitor) => `${visitor.firstname} ${visitor.lastname}`
              ),
            }
          })
        })
        .finally(() => (this.isLoadingPastSiteAccess = false))
    },

    selectRowTableSiteAccessDashboard(data) {
      this.$router.push(
        `/internal/web-portal/site-access/${data.id.siteAccessId}`
      )
    },

    filterDecoration(status) {
      let currentPageNumber
      switch (status) {
        case "access":
          currentPageNumber = this.filterData.currentPageTodaySiteAccess
          break
        case "approved":
          currentPageNumber = this.filterData.currentPageTodaySiteApproved
          break
        case "past":
          currentPageNumber = this.filterData.currentPagePastSiteAccess
          break
      }
      const filter = {
        PageSize: 10,
        PageNumber: currentPageNumber,
        SiteIds: this.siteOptions.map((site) => site.value),
      }
      return filter
    },

    changeCurrentPageTodaySiteApproved() {
      this.getTodaySiteApprovedFromApi()
    },

    changeCurrentPageTodaySiteAccess() {
      this.getTodaySiteAccessFromApi()
    },

    changeCurrentPagePastSiteAccess() {
      this.getPastSiteAccessFromApi()
    },

    dateFormat(dateTime) {
      if(!dateTime) {
        return null
      }
      return DateTime.fromISO(dateTime).toFormat("dd/MM/yyyy")
    },

    timeFormat(dateTime) {
      if(!dateTime) {
        return null
      }
      return DateTime.fromISO(dateTime).toFormat("HH:mm:ss")
    },
  },
}
</script>

<style lang="scss" scoped></style>
